import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/services/api-client";
import { cn } from "@/lib/utils";

export type Rec = Record<string, unknown>;
export const rec = (v: unknown): Rec =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {};
export const list = (v: unknown, keys: string[] = []): Rec[] => {
  if (Array.isArray(v)) return v.map(rec);
  const o = rec(v);
  for (const k of [...keys, "data", "items"])
    if (Array.isArray(o[k])) return (o[k] as unknown[]).map(rec);
  return [];
};
export const txt = (v: unknown, f = "") =>
  v === null || v === undefined || v === ""
    ? f
    : typeof v === "object"
      ? f
      : String(v);

export function errorMessage(
  e: unknown,
  fallback = "Une erreur est survenue.",
) {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.name !== "AbortError") return e.message;
  return fallback;
}

export function ErrorBox({
  message,
  onRetry,
  testId,
}: {
  message: string;
  onRetry?: () => void;
  testId?: string;
}) {
  return (
    <div
      role="alert"
      data-testid={testId ?? "error-box"}
      className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">{message}</span>
      {onRetry && (
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          className="gap-1.5"
        >
          <RotateCw className="h-3.5 w-3.5" /> Réessayer
        </Button>
      )}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}

export function Empty({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed p-5">
      <p className="text-sm font-semibold">{title}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      {children}
    </div>
  );
}

const TONES: Record<string, string> = {
  APPROVED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  APPROUVÉ: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  ACTIVE: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  ACTIF: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  COMPLETED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  CAPTURED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  SUBMITTED: "bg-blue-50 text-blue-700 ring-blue-600/20",
  SOUMIS: "bg-blue-50 text-blue-700 ring-blue-600/20",
  IN_REVIEW: "bg-blue-50 text-blue-700 ring-blue-600/20",
  UNDER_REVIEW: "bg-blue-50 text-blue-700 ring-blue-600/20",
  "EN COURS D'EXAMEN": "bg-blue-50 text-blue-700 ring-blue-600/20",
  PENDING: "bg-amber-50 text-amber-700 ring-amber-600/20",
  "EN ATTENTE": "bg-amber-50 text-amber-700 ring-amber-600/20",
  "EN ATTENTE DE SIGNATURE": "bg-amber-50 text-amber-700 ring-amber-600/20",
  SIGNED_RECEIVED: "bg-purple-50 text-purple-700 ring-purple-600/20",
  "CONTRAT SIGNÉ REÇU": "bg-purple-50 text-purple-700 ring-purple-600/20",
  SIGNED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  SIGNÉ: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  REJECTED: "bg-rose-50 text-rose-700 ring-rose-600/20",
  REJETÉ: "bg-rose-50 text-rose-700 ring-rose-600/20",
  FAILED: "bg-rose-50 text-rose-700 ring-rose-600/20",
  TERMINATED: "bg-rose-50 text-rose-700 ring-rose-600/20",
  RELEASED: "bg-slate-100 text-slate-700 ring-slate-500/20",
};
export function Chip({ value, testId }: { value: unknown; testId?: string }) {
  const s = txt(value, "INCONNU").toUpperCase();
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
        TONES[s] ?? "bg-amber-50 text-amber-700 ring-amber-600/20",
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {s}
    </span>
  );
}

/** Safe metadata only: drops nested objects that look like secrets. */
const SECRET =
  /(token|secret|password|pan|cvv|pin|card_number|number|credential|signature)/i;
export function MetaList({ data, omit = [] }: { data: Rec; omit?: string[] }) {
  const entries = Object.entries(data).filter(
    ([k, v]) =>
      !omit.includes(k) &&
      !SECRET.test(k) &&
      (v === null || typeof v !== "object"),
  );
  if (!entries.length) return null;
  return (
    <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-[minmax(120px,auto)_1fr]">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-mono text-muted-foreground">{k}</dt>
          <dd className="break-all font-medium">{txt(v, "—")}</dd>
        </div>
      ))}
    </dl>
  );
}
